/**
 * The one place `events-api`'s per-sport alert catalog and
 * `notification-api`'s per-fan on/off state meet on the client.
 *
 * `kinds` (from `/event/alert-kinds-get`) says what alert kinds this game
 * has. `alerts` (from `/notification-settings/get-game`) says which of them
 * this fan has turned on. The catalog side always wins the row list, so a
 * fan sees the correct five rows for a football game even while
 * `notification-api`'s own catalog is still a hardcoded two-entry fallback.
 */

export type typeGameAlertRow = {
  alertKindKey: string;
  label: string;
  isEnabled: boolean;
};

export type typeGameAlertsViewState = "loading" | "error" | "ready";

/**
 * The two kinds every sport reports, always first and in this order.
 * Module-private on purpose: no `export` keyword, nothing outside this file
 * reads it.
 */
const PINNED_ORDER: readonly string[] = ["gameStart", "gameEnd"];

export function resolveGameAlertsViewState(input: {
  isCatalogPending: boolean;
  isSettingsPending: boolean;
  catalogErrorCode: string | null;
  settingsErrorCode: string | null;
}): typeGameAlertsViewState {
  if (input.isCatalogPending || input.isSettingsPending) return "loading";
  if (input.catalogErrorCode !== null || input.settingsErrorCode !== null) {
    return "error";
  }
  return "ready";
}

export function buildGameAlertRows(input: {
  kinds: readonly { key: string; label: string; isOnByDefault: boolean }[];
  alerts: readonly { alertKindKey: string; isEnabled: boolean }[];
}): typeGameAlertRow[] {
  const settingsByKey = new Map<string, boolean>();
  for (const alert of input.alerts) {
    settingsByKey.set(alert.alertKindKey, alert.isEnabled);
  }

  const rows = input.kinds.map((kind) => ({
    alertKindKey: kind.key,
    label: kind.label,
    isEnabled: settingsByKey.get(kind.key) ?? kind.isOnByDefault,
  }));

  const pinned = PINNED_ORDER.map((key) =>
    rows.find((row) => row.alertKindKey === key),
  ).filter((row): row is typeGameAlertRow => row !== undefined);
  const rest = rows.filter((row) => !PINNED_ORDER.includes(row.alertKindKey));

  return [...pinned, ...rest];
}
